import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Dialog, DialogActions, DialogContent, Loading, Typography, } from '@guildeducationinc/recess';
import { isEmpty as _isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Messages, useLocale } from '../..';
import { classNames } from '../../../lib/utilities/appHelper';
import { FormFieldInput } from '../FormFieldInput';
import { TrackableButton } from '../TrackableButton/TrackableButton';
import styles from './Form.module.scss';
export const Form = ({ obj, fields, modal, title, embedded, onChange, errors, testable, onValidate, onSave, onTest, onCancel, children, error: initialError = '', minHeight = 300, }) => {
    const { t } = useLocale();
    const [draft, setDraft] = useState(obj);
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState(initialError);
    const [processing, setProcessing] = useState(false);
    const [messages, setMessages] = useState([]);
    const tag = title || 'form';
    const modalTag = `${tag}_modal`;
    const handleChange = (fieldInfo) => (value) => {
        const field = fieldInfo.name;
        setDraft(prev => ({
            ...prev,
            [field]: fieldInfo.deserializeValue ? fieldInfo.deserializeValue(value) : value,
        }));
    };
    useEffect(() => {
        setFieldErrors(errors ?? {});
    }, [errors]);
    useEffect(() => {
        if (onChange) {
            onChange(draft);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft]);
    useEffect(() => {
        setError(initialError);
    }, [initialError]);
    const handleSave = async () => {
        await handleSaveOrTest(onSave);
    };
    const handleTest = async () => {
        await handleSaveOrTest(onTest);
    };
    const handleSaveOrTest = async (onAction) => {
        if (!onValidate)
            return;
        setMessages([]);
        setError('');
        const errors = onValidate(draft);
        setFieldErrors(errors);
        if (!_isEmpty(errors)) {
            return;
        }
        if (!onAction)
            return;
        setProcessing(true);
        try {
            const errors = await onAction(draft);
            if (errors) {
                if (errors.messages) {
                    setMessages(errors.messages);
                }
                else {
                    // localize the errors
                    Object.keys(errors).forEach(key => {
                        errors[key] = t(errors[key]);
                    });
                    setFieldErrors(errors);
                }
            }
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setError(e.message);
        }
        finally {
            setProcessing(false);
        }
    };
    const formElements = fields.map(fieldInfo => (_jsx("div", { className: styles.field, children: !fieldInfo.hidden && (_jsx(FormFieldInput, { fieldInfo: fieldInfo, value: fieldInfo.serializeValue
                ? fieldInfo.serializeValue(draft[fieldInfo.name])
                : draft[fieldInfo.name] ?? '', errors: fieldErrors[fieldInfo.name], onChange: handleChange(fieldInfo) })) }, fieldInfo.name)));
    const handleCancel = () => {
        setError('');
        if (onCancel)
            onCancel();
    };
    if (!modal) {
        return (_jsx("div", { children: _jsxs(Loading, { label: '', loaded: !processing, overlay: true, showChildrenWhileLoading: true, children: [_jsx(Typography, { variant: 'h3', children: title && t(title) }), error && (_jsx(Alert, { display: true, hideClose: true, className: 'margin--bottom_16', type: 'danger', message: error })), _jsx(Messages, { messages: messages, className: 'margin--bottom_16' }), _jsxs("div", { className: classNames(embedded ? '' : 'margin--16'), children: [formElements, children && children({ draft, setDraft, errors: fieldErrors })] }), !embedded && (_jsxs("div", { className: 'flex flexJustify--space_between gap-3', children: [_jsxs("div", { className: 'flex gap-3', children: [testable && (_jsx(TrackableButton, { appearance: 'outline', enText: 'Test', tag: tag, onClick: handleTest, disabled: processing, children: t('Test') })), _jsx(TrackableButton, { appearance: 'outline', enText: 'Cancel', tag: tag, className: 'margin--left_16', onClick: handleCancel, disabled: processing, children: t('buttons.cancel') })] }), _jsx(TrackableButton, { enText: 'Submit', tag: tag, onClick: handleSave, disabled: processing, children: t('buttons.submit') })] }))] }) }));
    }
    return (_jsxs(Dialog, { isOpen: true, title: title && t(title), onClose: handleCancel, fullScreen: true, forceDisableFocusTrap: true, children: [_jsx(DialogContent, { children: _jsx("div", { style: { minHeight }, children: _jsxs(Loading, { label: '', loaded: !processing, overlay: true, showChildrenWhileLoading: true, children: [error && (_jsx(Alert, { display: true, hideClose: true, className: 'margin--bottom_16', type: 'danger', message: error })), _jsx(Messages, { messages: messages, className: 'margin--bottom_16' }), formElements, children && children({ draft, setDraft, errors: fieldErrors })] }) }) }), _jsxs(DialogActions, { fullScreen: false, children: [_jsx(TrackableButton, { enText: 'Cancel', tag: modalTag, appearance: 'outline', onClick: handleCancel, disabled: processing, children: t('buttons.cancel') }), testable && (_jsx(TrackableButton, { enText: 'Test', tag: modalTag, appearance: 'outline', onClick: () => handleSaveOrTest(onTest), disabled: processing, children: t('Test') })), _jsx(TrackableButton, { enText: 'Submit', tag: modalTag, onClick: () => handleSaveOrTest(onSave), disabled: processing, children: t('buttons.submit') })] })] }));
};
